import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api } from "../api";
import type { FeatureAccess } from "../features";
import { purposes } from "../reference-data";
import { haptic } from "../telegram";
import type { Candidate } from "../types";
import { useCardSwipe } from "../use-card-swipe";
import { ProfileVideoViewer } from "./ProfileVideo";
import { PageDialog } from "./ReferenceUI";
import { ReportDialog } from "./ReportDialog";

export function Discovery({
  onMatch,
  onFilters,
  onPremium,
}: {
  onMatch(): void;
  onFilters(): void;
  onPremium(): void;
}) {
  const [items, setItems] = useState<Candidate[]>([]);
  const [state, setState] = useState("loading");
  const [access, setAccess] = useState<FeatureAccess | null>(null);
  const [busy, setBusy] = useState(false);
  const actionPending = useRef(false);
  const [error, setError] = useState("");
  const [video, setVideo] = useState<string | null>(null);
  const closeVideo = useCallback(() => setVideo(null), []);
  const [photo, setPhoto] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [menu, setMenu] = useState(false);
  const [report, setReport] = useState(false);
  const [matchName, setMatchName] = useState<string | null>(null);
  const load = useCallback(async () => {
    setState("loading");
    try {
      const [r, rights] = await Promise.all([
        api<{ candidates: Candidate[] }>("/v1/discovery?limit=20"),
        api<FeatureAccess>("/v1/me/features").catch(() => null),
      ]);
      setAccess(rights);
      setItems(r.candidates);
      setPhoto(0);
      setExpanded(false);
      setState("ready");
    } catch (e) {
      setState(
        e instanceof ApiError && e.code === "premium_required"
          ? "premium"
          : e instanceof ApiError && e.code === "profile_under_review"
            ? "review"
            : e instanceof ApiError && e.code === "profile_hidden"
              ? "hidden"
              : "error",
      );
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const current = items[0];
  useEffect(() => {
    if (!current?.id) return;
    setPhoto(0);
    setExpanded(false);
    setMenu(false);
    setReport(false);
  }, [current?.id]);
  const swipe = async (decision: "like" | "pass" | "superlike") => {
    if (!current || actionPending.current) return;
    actionPending.current = true;
    setBusy(true);
    setError("");
    try {
      const r = await api<{ matched: boolean }>(`/v1/discovery/${current.id}/swipes`, {
        method: "POST",
        body: JSON.stringify({ decision }),
      });
      if (r.matched) setMatchName(current.displayName);
      setItems((v) => v.slice(1));
      haptic(r.matched ? "success" : decision === "pass" ? "pass" : "like");
      if (decision === "superlike") setAccess(await api<FeatureAccess>("/v1/me/features").catch(() => null));
    } catch (e) {
      if (e instanceof ApiError && e.code === "premium_required") onPremium();
      else if (e instanceof ApiError && e.code === "superlike_daily_limit")
        setError("На сегодня суперлайки закончились. Новые станут доступны после 03:00 по Москве.");
      else setError("Не удалось сохранить действие. Попробуйте ещё раз.");
    } finally {
      actionPending.current = false;
      setBusy(false);
    }
  };
  const rewind = async () => {
    if (!access?.features?.rewind) {
      onPremium();
      return;
    }
    if (actionPending.current) return;
    actionPending.current = true;
    setBusy(true);
    setError("");
    try {
      await api("/v1/me/rewind", { method: "POST" });
      await load();
    } catch (e) {
      if (e instanceof ApiError && e.code === "premium_required") onPremium();
      else if (e instanceof ApiError && e.code === "nothing_to_rewind") setError("Нет действий для возврата.");
      else if (e instanceof ApiError && ["cannot_rewind_match", "cannot_rewind_removed_pair"].includes(e.code))
        setError("Это действие уже нельзя отменить.");
      else setError("Не удалось вернуть анкету. Попробуйте ещё раз.");
    } finally {
      actionPending.current = false;
      setBusy(false);
    }
  };
  const block = async () => {
    if (!current || actionPending.current) return;
    actionPending.current = true;
    setBusy(true);
    setError("");
    try {
      await api(`/v1/users/${current.id}/block`, { method: "POST" });
      setItems((v) => v.slice(1));
      setMenu(false);
    } catch {
      setError("Не удалось заблокировать пользователя.");
    } finally {
      actionPending.current = false;
      setBusy(false);
    }
  };
  const gesture = useCardSwipe({
    cardId: current?.id,
    disabled: busy || state !== "ready" || menu || report || !!video || !!matchName,
    onSwipe: (decision) => void swipe(decision),
  });
  if (state === "loading") return <div className="card-skeleton" role="status" aria-label="Загрузка анкет" />;
  if (state !== "ready")
    return (
      <section className="empty-state page-empty">
        <h2>
          {state === "premium"
            ? "Фильтр видео требует Premium"
            : state === "review"
              ? "Фото на проверке"
              : state === "hidden"
                ? "Ваша анкета скрыта"
                : "Не удалось загрузить анкеты"}
        </h2>
        <p>
          {state === "premium"
            ? "Отключите фильтр «Только с видео» или откройте Premium."
            : state === "review"
              ? "Анкеты станут доступны после одобрения главной фотографии."
              : state === "hidden"
                ? "Включите видимость в настройках профиля."
                : "Проверьте соединение и повторите попытку."}
        </p>
        {state === "premium" && (
          <>
            <button type="button" className="reference-row" onClick={onFilters}>
              Изменить фильтры
            </button>
            <button type="button" className="reference-row" onClick={onPremium}>
              DonDate Premium
            </button>
          </>
        )}
        <button className="secondary-button" type="button" onClick={() => void load()}>
          Обновить
        </button>
      </section>
    );
  const photos = current?.photos?.length ? current.photos : current ? [current.photoUrl] : [];
  return (
    <>
      {video && <ProfileVideoViewer id={video} onClose={closeVideo} onPremium={onPremium} />}
      {current ? (
        <div className="reference-discovery">
          <article
            className={`reference-person-card swipe-card ${gesture.dragging ? "dragging" : ""}`}
            {...gesture.handlers}
            style={{
              transform: `translate3d(${gesture.offset}px,0,0) rotate(${Math.max(-9, Math.min(9, gesture.offset / 35))}deg)`,
            }}
            aria-label={`Анкета: ${current.displayName}`}
            aria-busy={busy}
          >
            {gesture.dragging && (
              <span className={`swipe-intent ${gesture.offset >= 0 ? "like" : "pass"}`} aria-hidden="true">
                {gesture.offset >= 0 ? "Нравится" : "Пропустить"}
              </span>
            )}
            {busy && (
              <span className="sr-only" role="status">
                Сохраняем действие…
              </span>
            )}
            <img
              className="reference-person-photo"
              draggable={false}
              src={photos[photo] ?? current.photoUrl}
              alt={`Фото ${photo + 1}: ${current.displayName}`}
            />
            <fieldset className="reference-photo-dots" aria-label="Фотографии">
              {photos.map((url, i) => (
                <button
                  type="button"
                  key={url}
                  className={i === photo ? "active" : ""}
                  aria-label={`Фотография ${i + 1}`}
                  aria-pressed={i === photo}
                  onClick={() => setPhoto(i)}
                />
              ))}
            </fieldset>
            {photos.length > 1 && (
              <div className="reference-photo-nav">
                <button
                  type="button"
                  aria-label="Предыдущее фото"
                  data-photo-navigation
                  onClick={() => setPhoto((photo + photos.length - 1) % photos.length)}
                >
                  ‹
                </button>
                <button
                  type="button"
                  data-photo-navigation
                  aria-label="Следующее фото"
                  onClick={() => setPhoto((photo + 1) % photos.length)}
                >
                  ›
                </button>
              </div>
            )}
            <button
              type="button"
              className="reference-rewind-button"
              aria-label="Вернуть последнюю анкету"
              disabled={busy}
              onClick={() => void rewind()}
            >
              ↶
            </button>
            <button
              type="button"
              className="reference-filter-button"
              aria-label="Открыть фильтры"
              disabled={busy}
              onClick={onFilters}
            >
              ☷
            </button>
            <div className="reference-person-copy">
              <h2>
                {current.displayName}
                {current.age !== null ? `, ${current.age}` : ""}{" "}
                {current.verified && (
                  <span className="verified" title="Профиль проверен">
                    ✓
                  </span>
                )}
              </h2>
              <div className="reference-tags">
                {[
                  purposes.find((p) => p.value === current.purpose)?.label,
                  current.station ?? current.city,
                  current.occupation,
                  current.heightCm ? `${current.heightCm} см` : null,
                ]
                  .filter(Boolean)
                  .map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
              </div>
              {current.hasVideo && current.videoId && (
                <button
                  type="button"
                  className="reference-watch-video"
                  onClick={() => {
                    if (!access?.features.video) onPremium();
                    else setVideo(current.videoId ?? null);
                  }}
                >
                  ▷ Смотреть видео{!access?.features.video ? " · Premium" : ""}
                </button>
              )}
              {current.bio && (
                <button
                  type="button"
                  className={`reference-bio ${expanded ? "expanded" : ""}`}
                  aria-expanded={expanded}
                  onClick={() => setExpanded(!expanded)}
                >
                  {current.bio}
                </button>
              )}
              <button type="button" className="reference-report-link" disabled={busy} onClick={() => setMenu(true)}>
                Пожаловаться
              </button>
            </div>
            {error && !menu && !report && !video && (
              <p role="alert" className="reference-card-error" data-no-swipe>
                {error}
              </p>
            )}
            <div className="reference-swipe-actions">
              <button
                type="button"
                className="pass"
                disabled={busy}
                aria-label="Пропустить"
                onClick={() => void swipe("pass")}
              >
                ×
              </button>
              <button
                type="button"
                className="super"
                aria-label={
                  access?.features?.superlikes
                    ? `Суперлайк, осталось ${access.superlikes.remaining}`
                    : "Возможности суперлайка"
                }
                disabled={busy}
                onClick={() => (access?.features?.superlikes ? void swipe("superlike") : onPremium())}
              >
                ✦
              </button>
              <button
                type="button"
                className="like"
                disabled={busy}
                aria-label="Нравится"
                onClick={() => void swipe("like")}
              >
                ♥
              </button>
            </div>
          </article>
        </div>
      ) : (
        <section className="empty-state page-empty">
          <h2>Пока всё</h2>
          <p>Подходящих анкет пока нет. Попробуйте изменить фильтры или загляните позже.</p>
          <button type="button" className="primary-button" onClick={onFilters}>
            Изменить фильтры
          </button>
          <button type="button" className="secondary-button" onClick={() => void load()}>
            Обновить
          </button>
          <button type="button" className="secondary-button" disabled={busy} onClick={() => void rewind()}>
            Вернуть последнюю анкету
          </button>
        </section>
      )}
      {error && !menu && !current && (
        <p role="alert" className="reference-error">
          {error}
        </p>
      )}
      {menu && current && (
        <PageDialog title="Безопасность" onClose={() => setMenu(false)} busy={busy}>
          <div className="reference-group">
            <button
              type="button"
              className="reference-row"
              onClick={() => {
                setMenu(false);
                setReport(true);
              }}
            >
              Пожаловаться <span>›</span>
            </button>
            <button type="button" className="reference-row" disabled={busy} onClick={() => void block()}>
              Заблокировать
            </button>
          </div>
          {error && (
            <p className="reference-error" role="alert">
              {error}
            </p>
          )}
        </PageDialog>
      )}
      {report && current && <ReportDialog userId={current.id} onClose={() => setReport(false)} />}
      {matchName && (
        <PageDialog title="Это взаимно" onClose={() => setMatchName(null)}>
          <div className="reference-match">
            <span aria-hidden="true">♡</span>
            <h2>Вы понравились друг другу</h2>
            <p>Начните разговор с {matchName}.</p>
            <button
              type="button"
              className="reference-primary"
              onClick={() => {
                setMatchName(null);
                onMatch();
              }}
            >
              Открыть совпадения
            </button>
            <button type="button" className="reference-row" onClick={() => setMatchName(null)}>
              Продолжить смотреть
            </button>
          </div>
        </PageDialog>
      )}
    </>
  );
}
